import { IconButton, Tooltip } from '@mui/material';
import Menu from '@mui/material/Menu';
import { Fragment, MouseEvent, default as React, useCallback, useState } from 'react';
import I18n, { useI18n } from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import type { ActionType } from '../ActionType';
import ResponsiveActionMenuItem from './ResponsiveActionMenuItem';
import { ResponsiveActionsProps } from './ResponsiveActions';

export interface PopoverResponsiveActionsProps extends Required<
    Pick<ResponsiveActionsProps, 'containerSx' | 'iconSx'>
> {
    actions: ActionType[];
}

export const PopoverResponsiveActions = ({
    actions,
    containerSx,
    iconSx
}: PopoverResponsiveActionsProps) => {
    const t = useI18n();
    const actionsMenuId = 'actions-menu-mobile';

    const isAnyActionActive = actions.some((action) => action.loading);

    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const areAllActionsHidden = actions.every((action) => action.hidden);

    const handleMobileMenuClose = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setIsMobileMenuOpen(false);
        setMobileMoreAnchorEl(null);
    }, []);

    const handleMobileMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMobileMoreAnchorEl(event.currentTarget);
        setIsMobileMenuOpen(true);
    }, []);

    return (
        <Fragment>
            <Tooltip title={<I18n token={'menu.responsive.showMore'} />}>
                <IconButton
                    aria-label={t('menu.responsive.showMore')}
                    aria-controls={actionsMenuId}
                    aria-haspopup="true"
                    aria-expanded={isMobileMenuOpen ? 'true' : undefined}
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '18px',
                        visibility: areAllActionsHidden ? 'hidden' : 'visible',
                        ...containerSx
                    }}
                    size="large"
                    onClick={handleMobileMenuOpen}
                    color="inherit"
                >
                    <Icons.MoreVert />
                </IconButton>
            </Tooltip>
            <Menu
                id={actionsMenuId}
                anchorEl={mobileMoreAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                open={isMobileMenuOpen}
                onClose={handleMobileMenuClose}
            >
                {actions.map((action, index) => (
                    <ResponsiveActionMenuItem
                        key={index}
                        action={action}
                        index={index}
                        isAnyActionActive={isAnyActionActive}
                        iconSx={iconSx}
                        onSelect={handleMobileMenuClose}
                    />
                ))}
            </Menu>
        </Fragment>
    );
};
